/**
 * Peers' level + rank for the players a screen is currently showing
 * (specs/rank-on-social-screens AC-15). Returns a lookup, not a list: every consumer wants
 * "what is this row's rank", never "all ranks".
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { XP_BATCH_MAX_USER_IDS, type PlayerRank } from '../constants/playerRanks.constants';
import { fetchPlayerRanks, type PlayerRankMap } from '../helpers/playerRanksApi';

export type PlayerRankLookup = (userId: string | null | undefined) => PlayerRank | null;

/**
 * @param userIds players on screen, **in the order they are rendered**.
 */
export function usePlayerRanks(userIds: string[]): { getPlayerRank: PlayerRankLookup } {
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const apiClient = useApiClient();

  const ids = useMemo(
    () =>
      [
        // The monkey earns no XP by design, so it would resolve to a permanent level 1 /
        // Rookie. Excluded from the request, not just hidden at render (AC-11).
        ...new Set(userIds.filter((id) => id != null && id !== '' && id !== MONKEY_USER_ID)),
      ]
        // Cap in CALLER order, so an over-cap list asks for the players at the top of the
        // screen (AC-15). Sorting first would pick 100 arbitrary ids by uuid — the friends
        // list is unbounded, so that is reachable, not theoretical.
        .slice(0, XP_BATCH_MAX_USER_IDS)
        // Sort only after capping: this makes the query key order-independent, so two screens
        // showing the same people share one cache entry.
        .sort(),
    [userIds],
  );

  const { data } = useQuery<PlayerRankMap>({
    queryKey: ['playerRanks', ids],
    queryFn: () => fetchPlayerRanks(apiClient, ids),
    enabled: isLoaded && isSignedIn === true && ids.length > 0,
    // Matches useXpSummary: a peer's level going stale for a minute is cosmetic.
    staleTime: 60 * 1000,
  });

  const getPlayerRank = useCallback<PlayerRankLookup>(
    (userId) => (userId == null ? null : (data?.[userId] ?? null)),
    [data],
  );

  return { getPlayerRank };
}
